import { apiFetch } from './api';

export interface PricingConfig {
    id: string;
    service_type: 'hotel' | 'flight' | 'taxi' | 'umrah' | 'hajj';
    markup_percentage: number;
    fixed_fee: number;
    currency: string;
}

export const bookingService = {
    // Pricing Configs
    async getPricingConfigs() {
        const data = await apiFetch('/admin/pricing');
        return data as PricingConfig[];
    },

    async updatePricingConfig(id: string, updates: Partial<PricingConfig>) {
        const data = await apiFetch(`/admin/pricing/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
        return data as PricingConfig;
    },

    // ─── Create a booking (after Paystack payment) ───────────
    async createBooking(bookingData: {
        payment_reference: string;
        service_type: 'flight' | 'hotel';
        amount: number;
        search_id?: string;
        offer_id?: string;
        passengers: any[];
        contact_email: string;
        contact_phone: string;
        booking_details?: any;
    }) {
        return apiFetch('/bookings', {
            method: 'POST',
            body: JSON.stringify(bookingData)
        });
    },

    // ─── Get current user's bookings ─────────────────────────
    async getMyBookings() {
        return apiFetch('/bookings/my');
    },

    // ─── Get single booking detail ───────────────────────────
    async getBooking(id: string) {
        return apiFetch(`/bookings/${id}`);
    },

    // ─── Admin: Get all bookings ─────────────────────────────
    async getBookings() {
        return apiFetch('/admin/bookings');
    },

    // ─── Admin: Update booking status ────────────────────────
    async updateBookingStatus(bookingId: string, status: string) {
        return apiFetch(`/admin/bookings/${bookingId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status })
        });
    },

    // ─── Admin: Retry LetSFG booking fulfillment ─────────────
    async retryLetSFGBooking(bookingId: string) {
        return apiFetch(`/admin/bookings/${bookingId}/retry-letsfg`, {
            method: 'POST'
        });
    },

    // ─── Admin: Manually issue confirmation PNR ──────────────
    async manualIssueBooking(bookingId: string, pnr: string, notes?: string) {
        return apiFetch(`/admin/bookings/${bookingId}/manual-issue`, {
            method: 'POST',
            body: JSON.stringify({ pnr, notes })
        });
    },

    // ─── Get Paystack Public Key ─────────────────────────────
    async getPaymentConfig() {
        return apiFetch('/bookings/payment-config');
    },

    // ─── Admin: Delete a booking permanently ──────────────────
    async deleteBooking(bookingId: string) {
        return apiFetch(`/admin/bookings/${bookingId}`, {
            method: 'DELETE'
        });
    },

    // ─── Verify reference for Callback URL ────────────────────
    async verifyBookingReference(reference: string) {
        return apiFetch(`/bookings/verify/${encodeURIComponent(reference)}`);
    },

    // ─── Record an incomplete or cancelled payment attempt ───
    async recordFailedAttempt(data: {
        payment_reference?: string;
        service_type: string;
        amount: number;
        reason?: string;
        booking_details?: any;
        contact_email?: string;
        contact_phone?: string;
    }) {
        return apiFetch('/bookings/failed-attempt', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },
};
